// @flow strict

import React from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { bindActionCreators } from 'redux';
import { injectIntl } from 'react-intl';

import { MicroFrontEnds } from '../../microfrontendURIs';

import MicroFrontend from '..';
import { ContainerCentered } from 'styles/common';
import ErrorMessage from 'components/ErrorMessage';
import { QuickNavigatorObservable } from 'components/_FrontendObservables';

import type { IntlType } from 'types';
import type { ImmutableUser } from 'services/Authentication/types';
import { canAccessPlantImportFeatures } from 'utils/authentication';

import { selectUser, selectUserTimezone } from 'services/Authentication/selectors';
import { selectAllClients } from 'services/Client/selectors';
import { fetchAllClients } from 'services/Client/thunks';

type Props = {
    intl: IntlType,
    userTimezone: string,

    // Injected:
    user: ImmutableUser,
    clients: ImmutableList<ImmutableClient>,
    fetchAllClients: () => void, // for navigation.
};

type State = {
    isUnLoadable: boolean,
};

/**
 * PlantImport MFE - This micro frontend is used to import plant data manually.
 */
class PlantImport extends React.PureComponent<Props, State> {
    state = {
        isUnLoadable: false,
    };

    static getHost = () => {
        return MicroFrontEnds.plantImport; // name must match the secret object's structure in GCP
    };

    static getUniqueName = () => {
        return 'PlantImportMFE'; // name must match the container name in the MFE code
    };

    componentDidMount() {
        // for navigation.
        if (this.props.clients.isEmpty()) {
            this.props.fetchAllClients();
        }

        QuickNavigatorObservable.setQuickNavigatorSolvExtract();

    }

    componentWillUnmount() {
        QuickNavigatorObservable.removeQuickNavigator()
    }

    getMountProps = () => {
        return {
            locale: this.props.intl.locale.toUpperCase(),
            userTimezone: this.props.userTimezone,
        };
    };

    onError = () => {
        this.setState({ isUnLoadable: true });
    };

    render() {
        if (!canAccessPlantImportFeatures(this.props.user)) {
            return (
                <ContainerCentered>
                    <ErrorMessage
                        errorMessage={this.props.intl.formatMessage({
                            id: 'views.ReagentsManagementMFE.userMustBeAdmin',
                        })}
                    />
                </ContainerCentered>
            );
        }

        if (this.state.isUnLoadable) {
            return (
                <ContainerCentered>
                    <ErrorMessage
                        errorMessage={this.props.intl.formatMessage({
                            id: 'views.PlantImportMFE.anErrorOccurredDuringLoad',
                        })}
                    />
                </ContainerCentered>
            );
        }

        return (
            <MicroFrontend
                name={PlantImport.getUniqueName()}
                host={PlantImport.getHost()}
                mountedMicroFrontendProps={this.getMountProps()}
                onError={this.onError}
                mfeIsInFullScreen
            />
        );
    }
}

const mapStateToProps = () =>
    createStructuredSelector({
        user: selectUser(),
        userTimezone: selectUserTimezone(),
        clients: selectAllClients(),
    });

    const mapDispatchToProps = (dispatch: ReduxDispatch) =>
        bindActionCreators(
            {
                fetchAllClients,
            },
            dispatch
        );

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(injectIntl(PlantImport))
